/**
 * SKU Supplemental Agreement & Deed of Charge Signing Component
 * Handles document viewing and signing with optional liveness verification for loan applications
 */

import { FC, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Center, Loader, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ToastContainer, toast } from "react-toastify";
import DocViewer from "@cyntler/react-doc-viewer";
import "react-toastify/dist/ReactToastify.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Button } from "@funded-here-interface/common";
import { setRegtankLiveness } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { LivenessWebVerifyStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { isValidRegtankUrl } from "@funded-here-interface/common/src/Utils/urlValidation";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import { useDocumentVisibility } from "@funded-here-interface/common/src/hooks/useDocumentVisibility";

import { RootState } from "../../store";
import { WEB_DOMAIN } from "../../constants/domain";
import { useLoanSign } from "../../hooks/useDocuSign";
import useGetLivenessWebVerifyLink from "../../hooks/useGetLivenessWebVerifyLink";
import useGetRegtankLivenessWebVerifyStatus from "../../hooks/useGetRegtankLivenessWebVerifyStatus";
import useGetSkusPendingMerchantSigning from "../../hooks/useGetSkusPendingMerchantSigning";
import { useRegtankLivenessSession } from "../../hooks/useRegtankLivenessSession";
import { useRegtankLivenessValidation } from "../../hooks/useRegtankLivenessValidation";

import { useStyle } from "./LoanApplication.styles";
import {
  useDocumentState,
  useButtonState,
  FailedLivenessModal,
  COLORS,
  MESSAGES,
} from "../../components/DocumentSigning";
import { AGREEMENT_NAME } from "../../components/DocumentSigning/constants";
import { useHandleSign } from "../../components/DocumentSigning/hooks/useHandleDocumentSign";
const LoanApplication: FC = () => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    token,
    hasCreditLimit,
    regtankLiveness,
    regtankLivenessFeatureFlag,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    regtankRequest,
  } = useSelector((state: RootState) => state.auth);

  // API hooks
  const docuSignMutation = useLoanSign();
  const skusPendingSigningMutation = useGetSkusPendingMerchantSigning();
  const livenessLinkMutation = useGetLivenessWebVerifyLink();
  const livenessStatusMutation = useGetRegtankLivenessWebVerifyStatus();
  const { data, refetch } = useGetUser(token);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [skuIds, setSkuIds] = useState<any>([]);
  const [regtankLivenessUrl, setRegtankLivenessUrl] = useState("");
  const [openedFailedLiveness, modalAction] = useDisclosure();

  // Custom hooks
  const documentState = useDocumentState();
  const livenessSession = useRegtankLivenessSession(
    documentState.documentId,
    data?.user?.regtankLiveness || regtankLiveness
  );
  const livenessValidation = useRegtankLivenessValidation(livenessSession);
  const buttonState = useButtonState({
    regtankLivenessFeatureFlag: regtankLivenessFeatureFlag ?? null,
    regtankOnboardingFeatureFlag: regtankOnboardingFeatureFlag ?? null,
    regtankOnboarding: regtankOnboarding ?? null,
    regtankRequest,
    isDisabledByLiveness: livenessValidation.isDisabled,
  });
  // Document viewer config
  const docs = [{ uri: documentState.realUrl, fileType: "pdf" as const }];
  const redirectUrl = window.location.href;

  /**
   * Initial load: Fetch pending SKUs and restore document state from sessionStorage if available
   */
  useEffect(() => {
    setIsLoading(true);
    skusPendingSigningMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          const skuIds = data.map((sku: any) => sku.id.toString());
          setSkuIds(skuIds);
          const getDocUrl = sessionStorage.getItem("realUrl");
          const getDocumentId = sessionStorage.getItem("documentId");
          const getCompletedAt = sessionStorage.getItem("completedAt");
          if (
            getDocUrl !== null &&
            getCompletedAt !== null &&
            getDocumentId !== null
          ) {
            documentState.updateDocument(
              getDocUrl,
              parseInt(getDocumentId),
              getCompletedAt
            );
          } else {
            refetch();
            handleGenerateDocument(skuIds);
          }
          setIsLoading(false);
        },
        onError: (e) => {
          setIsLoading(false);
          toast.error((e as Error).message);
        },
      }
    );
    documentState.incrementViewerKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentVisibility(
    () => documentState.incrementViewerKey(),
    !!documentState.documentId
  );

  /**
   * Generate SKU Supplemental Agreement & Deed of Charge document
   */
  const handleGenerateDocument = async (skuIds: any) => {
    toast.info(MESSAGES.LOADING_DOC, { toastId: "sku-agreement-load-doc" });

    if (skuIds.length > 0) {
      docuSignMutation.mutate(
        {
          token,
          poolingSkuIds: skuIds,
          webDomain: WEB_DOMAIN,
          redirectUrl: `${getBaseUrl(Microservice.DOCUMENT_MANAGEMENT)}/document-management/docusign/webhook`,
          regtankLivenessFeatureFlag: regtankLivenessFeatureFlag || undefined,
        },
        {
          onSuccess: async (data) => {
            documentState.updateDocument(
              data.url,
              data.documentId,
              data.completedAt
            );
            documentState.incrementViewerKey();
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    }
  };

  /**
   * Navigate to next page based on credit limit status and clean up sessionStorage
   */
  const navigateNext = () => {
    navigate("/collaboration-proposal/agreement");
    sessionStorage.removeItem("realUrl");
    sessionStorage.removeItem("documentId");
    sessionStorage.removeItem("completedAt");
    sessionStorage.removeItem("regtankSessionId");
  };

  /**
   * Sign the document after submitting agreements
   */
  const handleSign = useHandleSign({
    documentState,
    regtankLivenessFeatureFlag,
    token,
    needToCompleteOnboarding: false,
    needToSubmitAgreements: true,
    skuIds: skuIds,
  });

  /**
   * Handle liveness verification or direct signing with sessionStorage persistence
   */
  const handleLivenessVerify = async () => {
    buttonState.disableButton("");

    if (!regtankLivenessFeatureFlag) {
      handleSign();
      return;
    }
    await refetch();
    // Use existing liveness URL if available
    if (regtankLivenessUrl) {
      if (!isValidRegtankUrl(regtankLivenessUrl)) {
        toast.error(MESSAGES.INVALID_URL);
        return;
      }
      window.open(regtankLivenessUrl, "_self");
      return;
    }

    // Generate new liveness URL
    toast.info(MESSAGES.LOADING_LIVENESS, {
      toastId: "loading-regtank-liveness-check-url",
    });

    livenessLinkMutation.mutate(
      {
        token,
        documentId: documentState.documentId,
        redirectUrl,
        agreementName: AGREEMENT_NAME.LOAN_AGREEMENT,
      },
      {
        onSuccess: (data) => {
          if (!isValidRegtankUrl(data.verifyLink)) {
            toast.error(MESSAGES.INVALID_URL);
            return;
          }

          // Persist to sessionStorage for page refresh
          sessionStorage.setItem("regtankSessionId", data.sessionId);
          sessionStorage.setItem("realUrl", documentState.realUrl);
          sessionStorage.setItem(
            "documentId",
            documentState.documentId.toString()
          );
          sessionStorage.setItem("completedAt", documentState.completedAt);

          dispatch(
            setRegtankLiveness({
              regtankLiveness: [
                {
                  id: documentState.documentId.toString(),
                  clientSessionId: documentState.documentId.toString(),
                  sessionId: data.sessionId,
                  livenessStatus: null,
                  regtankLivenessUrl: data.verifyLink,
                  noOfAttemptLiveness: 0,
                  reattemptApproval: null,
                  expiredDate: data.expiredDate,
                },
              ],
            })
          );

          window.open(data.verifyLink, "_self");
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
          documentState.incrementViewerKey();
        },
      }
    );
  };

  /**
   * Handle modal continue action after failed liveness
   */
  const handleContinue = () => {
    buttonState.disableButton(MESSAGES.VERIFICATION_DISABLED);
    modalAction.close();
    sessionStorage.removeItem("regtankSessionId");
    documentState.incrementViewerKey();
  };

  // Verify liveness status when needed
  useEffect(() => {
    if (!livenessSession || !livenessValidation.shouldVerify) return;

    livenessStatusMutation.mutate(
      { token, sessionId: livenessSession.sessionId },
      {
        onSuccess: (data: any) => {
          if (data === LivenessWebVerifyStatusEnum.SUCCESS) {
            handleSign();
          } else if (
            data === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
            data === LivenessWebVerifyStatusEnum.FAILED
          ) {
            modalAction.open();
          }
          documentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livenessSession, livenessValidation.shouldVerify, token]);

  // Set liveness URL when user can attempt verification
  useEffect(() => {
    if (livenessValidation.canAttempt) {
      setRegtankLivenessUrl(livenessSession?.regtankLivenessUrl || "");
    }
  }, [livenessValidation.canAttempt, livenessSession]);

  return (
    <>
      <div className={classes.stepContent} style={{ marginTop: "0px" }}>
        <Text
          className={classes.headerText}
          style={{ marginTop: "10px", width: "140%" }}
        >
          {AGREEMENT_NAME.LOAN_AGREEMENT}
        </Text>

        {documentState.isDocumentLoaded && (
          <>
            <DocViewer
              documents={docs}
              style={{ width: "100%", height: 500, overflow: "auto" }}
              key={documentState.viewerKey}
              className={classes.docviewer}
              prefetchMethod="GET"
              config={{
                header: {
                  disableHeader: false,
                  disableFileName: true,
                  retainURLParams: false,
                },
                pdfVerticalScrollByDefault: true,
              }}
            />

            {!documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Text style={{ marginBottom: 10, fontSize: 14 }}>
                  {MESSAGES.ACKNOWLEDGEMENT}
                </Text>
                <Button
                  disabled={buttonState.buttonDisabled}
                  onClick={handleLivenessVerify}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                  title={buttonState.titleButton}
                >
                  Proceed to Sign
                </Button>
              </div>
            )}

            {documentState.isDocumentSigned && (
              <div className={classes.buttonContainer}>
                <Button
                  onClick={navigateNext}
                  textColor="white"
                  backgroundColor={COLORS.PRIMARY}
                >
                  Continue
                </Button>
              </div>
            )}

            <FailedLivenessModal
              opened={openedFailedLiveness}
              onClose={modalAction.close}
              onContinue={handleContinue}
              classes={classes}
            />
          </>
        )}

        {!documentState.isDocumentLoaded && (
          <Center maw={400} h={500} mx="auto">
            <Loader color="teal" />
          </Center>
        )}
      </div>

      <ToastContainer />
    </>
  );
};

export default LoanApplication;
